import React, { useState, useEffect, useCallback, useRef } from 'react';

const OFFER_TIMER_DURATION = 20 * 60; // 10 minutes in seconds
const SESSION_STORAGE_KEY = 'offerTimerStart';
const BROADCAST_CHANNEL_NAME = 'offer-timer-sync';

/**
 * Pure function to check whether the limited-time offer is still available.
 * Reads directly from sessionStorage — works outside of React too.
 * Returns `true` if the timer hasn't started yet OR if time remains.
 */
export function isOfferAvailable(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);

  if (!stored) {
    // Timer hasn't been initialised yet — the offer is available
    return true;
  }

  const elapsed = Math.floor((Date.now() - parseInt(stored, 10)) / 1000);
  return elapsed < OFFER_TIMER_DURATION;
}

/**
 * Returns the number of seconds remaining on the offer timer.
 * Returns 0 if expired, OFFER_TIMER_DURATION if not yet started.
 */
function getTimeLeft(): number {
  if (typeof window === 'undefined') {
    return OFFER_TIMER_DURATION;
  }

  const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);

  if (!stored) {
    // Initialise the timer on first access
    sessionStorage.setItem(SESSION_STORAGE_KEY, Date.now().toString());
    return OFFER_TIMER_DURATION;
  }

  const elapsed = Math.floor((Date.now() - parseInt(stored, 10)) / 1000);
  const remaining = OFFER_TIMER_DURATION - elapsed;
  return remaining > 0 ? remaining : 0;
}

/**
 * Shared hook so every mounted timer component ticks from the same
 * sessionStorage source-of-truth.  A BroadcastChannel is used to nudge
 * other tabs/instances when the timer starts.
 */
export function useOfferTimer() {
  const [timeLeft, setTimeLeft] = useState<number>(getTimeLeft);
  const channelRef = useRef<BroadcastChannel | null>(null);

  // Sync across tabs / instances via BroadcastChannel
  useEffect(() => {
    try {
      const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      channelRef.current = channel;

      channel.onmessage = () => {
        // Another instance updated — re-read from sessionStorage
        setTimeLeft(getTimeLeft());
      };

      return () => {
        channel.close();
      };
    } catch {
      // BroadcastChannel not supported — single-tab still works fine
    }
  }, []);

  // Countdown interval
  useEffect(() => {
    if (timeLeft <= 0) {
      return;
    }

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft > 0]); // restart only when transitioning from 0 → positive

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  return {
    timeLeft,
    isActive: timeLeft > 0,
    isUrgent: timeLeft > 0 && timeLeft <= 120,
    formattedTime: formatTime(timeLeft),
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

interface OfferTimerProps {
  /** When true, renders a compact inline pill suitable for headers / buttons */
  mini?: boolean;
}

/**
 * Countdown timer pill for the limited-time offer.
 *
 * - Default variant: the full pill used inside pricing cards
 * - `mini` variant: a small inline badge for the header Upgrade button
 *
 * All instances share state via `useOfferTimer` (sessionStorage-backed).
 */
export default function OfferTimer({ mini = false }: OfferTimerProps) {
  const { isActive, formattedTime } = useOfferTimer();

  if (!isActive) {
    return null;
  }

  // ── Mini variant ──────────────────────────────────────────────────────────
  if (mini) {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold tabular-nums whitespace-nowrap"
        style={{
          background: 'rgba(255, 23, 68, 0.85)',
          color: '#fff',
          animation: 'offerPulse 1.5s ease-in-out infinite',
          lineHeight: '1.2',
        }}
      >
        <svg
          className="w-2.5 h-2.5 flex-shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
        {formattedTime}

        {/* Inject keyframes once (harmless if duplicated) */}
        <style>{`
          @keyframes offerPulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.75; }
          }
        `}</style>
      </span>
    );
  }

  // ── Default (full) variant ────────────────────────────────────────────────
  return (
    <div
      className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full"
      style={{
        background: 'rgba(255, 23, 68, 0.85)',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(255, 23, 68, 0.4)',
        animation: 'offerPulse 1.5s ease-in-out infinite',
      }}
    >
      <svg
        className="w-3 h-3 flex-shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
      <span
        className="text-white text-[10px] font-semibold whitespace-nowrap"
        style={{ fontFamily: 'Inter Display' }}
      >
        Offer ends in
      </span>
      <span
        className="text-white text-xs font-bold tabular-nums"
        style={{
          fontFamily: 'Inter Display',
          letterSpacing: '0.5px',
        }}
      >
        {formattedTime}
      </span>

      <style>{`
        @keyframes offerPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.75; }
        }
      `}</style>
    </div>
  );
}
